import { buildColorAccessor, buildFirstThresholdMeasureAccessor, buildMeasureAccessor, buildTimestampAccessor, computeMetricComparison, DataPointsApiResponse, getLatestValue, getLineStyleForDefaultThresholdLine, getMeasureFormatString, getStylePropsForDefaultThresholdLine, LineStyle, MetricBlue, MetricsNeutralColor, shouldRenderFirstThresholdLine } from '@tableau/metrics-engine';
import { MetricValuePill, SparkLine } from '@tableau/metrics-shared-widgets-dom';
import * as React from 'react';

import { cssClass } from '../../style';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { getMetricMeasure, getTableWithDefaultComparison } from '..';

export type MetricSparklineAndPillProps = {
    readonly metricData: DataPointsApiResponse;
};

export class MetricSparklineAndPill extends React.PureComponent<MetricSparklineAndPillProps> {
    public render(): JSX.Element {
        const definition = this.props.metricData.definition;
        const timestampAccessor = buildTimestampAccessor(definition);
        const measureAccessor = buildMeasureAccessor(definition);
        const colorAccessor = buildColorAccessor(definition);
        const comparisonAccessor = buildFirstThresholdMeasureAccessor(definition);

        let measure;
        let format;

        if (this.props.metricData && this.props.metricData.data && this.props.metricData.data.length > 0) {
          measure = getMetricMeasure(this.props.metricData, measureAccessor);
          format = getMeasureFormatString(definition);
        }

        const showSparkline = this.props.metricData && this.props.metricData.data && this.props.metricData.data.length > 1 && timestampAccessor;

        const comparisonTable = this.props.metricData && getTableWithDefaultComparison(this.props.metricData);

        const latestValue = comparisonTable && getLatestValue(comparisonTable);
        const comparison = latestValue && computeMetricComparison(latestValue, measureAccessor, comparisonAccessor);

        const colorsEnabledColor =
          latestValue && comparison
            ? colorAccessor(comparison.comparedPoint)
            : MetricsNeutralColor;

        const color = FeatureFlags.metricsColorIndicatorsEnabled ? colorsEnabledColor : MetricBlue;
        const thresholdLineStyle = getLineStyleForDefaultThresholdLine(definition);
        const lineStyle = thresholdLineStyle?.lineStyle;
        const comparisonLineStyle = {
          ...getStylePropsForDefaultThresholdLine(definition),
          ...(lineStyle === LineStyle.DASHED ? { strokeWidth: 3 } : {}),
        };

        const shouldRenderReferenceLine = FeatureFlags.metricsReferenceLineEnabled && shouldRenderFirstThresholdLine(definition);
        return (
        <div data-tb-test-id="omnibox-metric-thumbnail">
            <span className={MetricOmniPillStyle} style={FeatureFlags.metricsColorIndicatorsEnabled ? {paddingTop: 2} : {}} data-tb-test-id="omnibox-metric-pill">
              <MetricValuePill
                value={measure}
                measureFormat={format}
                color={color}
                colorIndicatorsEnabled={FeatureFlags.metricsColorIndicatorsEnabled}
                leftAlign={false}
              />
            </span>
            {
              showSparkline &&
              <SparkLine
                size={MetricOmniSparklineSize}
                data={comparisonTable}
                timestampAccessor={timestampAccessor}
                measureAccessor={measureAccessor}
                comparisonAccessor={FeatureFlags.metricsReferenceLineEnabled ? comparisonAccessor : undefined}
                style={MetricOmniSparklineStyle}
                color={color}
                testId={'omnibox-metric-sparkline'}
                shouldRenderReferenceLine={shouldRenderReferenceLine}
                shouldRenderComparisonDot={!thresholdLineStyle?.hidden && thresholdLineStyle?.interactive}
                formatString={format}
                comparisonLineStyle={comparisonLineStyle}
                colorIndicatorsEnabled={FeatureFlags.metricsColorIndicatorsEnabled}
              />
            }
            {
              !showSparkline && <div style={{...MetricOmniSparklineStyle, ...MetricOmniSparklineSize}} />
            }
        </div>);
    }
}

const thumbnailWidth = 76;
const sparklineHeight = 18;

const MetricOmniPillStyle = cssClass('omni', 'metric-pill', {
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  paddingTop: 8,
  alignItems: 'center',
  justifyContent: 'center',
  width: thumbnailWidth + 'px',
  height: '20px',
});

const MetricOmniSparklineStyle = {paddingTop: '8px', paddingBottom: '8px'};
const MetricOmniSparklineSize = { width: thumbnailWidth, height: sparklineHeight};
